/**
 * Zuordnung "Feldpfad -> Tab" fuer die Fehler-Kennzeichnung an Formular-Reitern (D-06).
 *
 * Zweck: nach einem Einreichen-Klick markiert das Backend die unvollstaendigen Felder rot.
 * Liegen alle markierten Felder auf einem anderen Reiter, sieht der Klick fuer den Nutzer
 * wirkungslos aus. Diese Zuordnung beantwortet die Frage "welche Reiter tragen Fehler",
 * damit genau diese Reiter gekennzeichnet werden koennen.
 *
 * Bewusst formularunabhaengig -- und seit Plan 20-16 bekommt es seine Karte nicht mehr aus
 * einer handgepflegten Feldliste, sondern aus der Render-Ableitung in
 * `components/form/fieldTabRegistry.ts`: die geteilten Feldkomponenten melden ihren
 * Feldnamen beim Mounten an den Reiter, in dem sie stehen. Dieses Modul bleibt davon
 * unberuehrt, es rechnet weiterhin nur "Fehlerobjekt + Karte -> betroffene Reiter".
 *
 * Ausdruecklich NICHT Teil dieses Moduls (D-06): automatischer Reiterwechsel, Sprung
 * zum ersten Fehler, zusammenfassende Fehlerliste.
 */

/**
 * Tab-Wert (der Wert des jeweiligen `Tabs.Tab`) -> Feldpfad-Praefixe, die auf diesem
 * Reiter gerendert werden. Ein Eintrag trifft einen Fehlerschluessel, wenn dieser dem
 * Eintrag exakt entspricht oder mit `<eintrag>.` beginnt — damit deckt das Praefix
 * `rotaryKilns` auch `rotaryKilns.0.measurements.0.dustValue` ab.
 */
export type TabFieldMap = Record<string, readonly string[]>;

/**
 * Liefert die Tab-Werte, auf denen mindestens ein Feld einen Fehler traegt.
 *
 * `errors` ist das Mantine-Fehlerobjekt (`form.errors`). Mantine belaesst geleerte
 * Fehler teils als `undefined` im Objekt — solche Schluessel zaehlen bewusst nicht.
 */
export function hasFieldErrors(errors: Record<string, unknown>): boolean {
  // Dieselbe Falsy-Regel wie in tabsWithErrors: Mantine belaesst geleerte Fehler teils als
  // `undefined` im Objekt, ein blosses `Object.keys(...).length > 0` meldete deshalb Fehler,
  // wo keine mehr sind.
  return Object.values(errors).some(Boolean);
}

export function tabsWithErrors(
  errors: Record<string, unknown>,
  map: TabFieldMap
): Set<string> {
  const tabs = new Set<string>();

  for (const [key, value] of Object.entries(errors)) {
    if (!value) continue;

    for (const [tab, fields] of Object.entries(map)) {
      if (tabs.has(tab)) continue;
      if (fields.some((field) => key === field || key.startsWith(`${field}.`))) {
        tabs.add(tab);
      }
    }
  }

  return tabs;
}

/**
 * Verkettet die Fehlermeldungen der uebergebenen Feldpfade zu einer einzigen Zeichenkette.
 * Dient AUSSCHLIESSLICH als Re-Render-Ausloeser fuer memoisierte Teilbaeume (`memo()`-Comparator):
 * bleibt die Signatur zwischen zwei Renders gleich, hat sich an keinem der uebergebenen Pfade
 * etwas geaendert, und die memoisierte Karte darf das Re-Render ueberspringen.
 *
 * Dies ist die dritte Fundstelle desselben Musters im Projekt (`rowErrorSig` in
 * `BrennstoffeSection.tsx`, `measurementErrorSig` in `KilnMeasurementCard.tsx`) -- beide aeltere
 * Kopien bleiben bewusst unangetastet, weil sie ausserhalb dieses Phasenumfangs liegen. Das ist
 * eine offene Vereinheitlichung, kein Versehen.
 *
 * Die Vollstaendigkeit der `paths`-Liste ist sicherheitsrelevant: fehlt ein Feldpfad, bleibt ein
 * vom Backend abgelehnter Wert auf genau diesem Feld unsichtbar, weil die memoisierte Karte nicht
 * neu rendert (T-19-27/T-19-28).
 */
export function fieldErrorSig(errors: Record<string, unknown>, paths: readonly string[]): string {
  return paths.map((p) => String(errors[p] ?? '')).join('|');
}
